/**
 * Thumbnail Slider Component
 * Vertical thumbnail navigation with height synced to main product image.
 * Shows up/down arrows when thumbnails exceed container height.
 */
export default function thumbnailSlider(config = {}) {
  return {
    // Configuration
    totalThumbs: config.totalThumbs || 1,
    thumbWidth: 80, // w-20 = 80px
    thumbGap: config.thumbGap || 12, // gap-3 = 12px
    aspectRatio: config.aspectRatio || '4/5',

    // State
    scrollOffset: 0,
    containerHeight: 400,
    thumbHeight: 0,
    maxScroll: 0,
    canScrollUp: false,
    canScrollDown: false,

    init() {
      // Calculate thumb height based on aspect ratio
      this.calculateThumbHeight();

      // Get main image container height and sync
      this.$nextTick(() => {
        this.syncWithMainImage();
        this.calculateScrollBounds();
      });

      // Recalculate on window resize
      window.addEventListener('resize', () => {
        this.syncWithMainImage();
        this.calculateScrollBounds();
      });

      // Listen for image changes via custom event (from product component)
      document.addEventListener('product-image-changed', (e) => {
        if (typeof e.detail?.index === 'number') {
          this.scrollToThumb(e.detail.index);
          this.updateActiveThumbnail(e.detail.index);
        }
      });

      // Setup mouse wheel scrolling
      this.$nextTick(() => {
        this.setupWheelScroll();
        this.setupTouchScroll();
      });
    },

    /**
     * Setup mouse wheel scrolling
     */
    setupWheelScroll() {
      const container = this.$refs.thumbSliderContainer;
      if (!container) return;

      container.addEventListener('wheel', (e) => {
        // Only handle if there's scrollable content
        if (this.maxScroll <= 0) return;

        e.preventDefault();

        // Calculate scroll amount based on wheel delta
        const scrollAmount = Math.sign(e.deltaY) * (this.thumbHeight + this.thumbGap);

        this.scrollOffset = Math.max(0, Math.min(this.maxScroll, this.scrollOffset + scrollAmount));
        this.updateScrollButtons();
      }, { passive: false });
    },

    /**
     * Setup touch/drag scrolling
     */
    setupTouchScroll() {
      const container = this.$refs.thumbSliderContainer;
      if (!container) return;

      let startY = 0;
      let startOffset = 0;
      let isDragging = false;

      // Touch events
      container.addEventListener('touchstart', (e) => {
        if (this.maxScroll <= 0) return;
        startY = e.touches[0].clientY;
        startOffset = this.scrollOffset;
        isDragging = true;
      }, { passive: true });

      container.addEventListener('touchmove', (e) => {
        if (!isDragging || this.maxScroll <= 0) return;

        const currentY = e.touches[0].clientY;
        const deltaY = startY - currentY;

        this.scrollOffset = Math.max(0, Math.min(this.maxScroll, startOffset + deltaY));
        this.updateScrollButtons();
      }, { passive: true });

      container.addEventListener('touchend', () => {
        isDragging = false;
      }, { passive: true });

      // Mouse drag events
      container.addEventListener('mousedown', (e) => {
        if (this.maxScroll <= 0) return;
        // Only start drag on the container, not on thumbnails
        if (e.target.closest('.gallery-thumbnail')) return;

        startY = e.clientY;
        startOffset = this.scrollOffset;
        isDragging = true;
        container.style.cursor = 'grabbing';
      });

      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;

        const deltaY = startY - e.clientY;
        this.scrollOffset = Math.max(0, Math.min(this.maxScroll, startOffset + deltaY));
        this.updateScrollButtons();
      });

      document.addEventListener('mouseup', () => {
        if (isDragging) {
          isDragging = false;
          container.style.cursor = '';
        }
      });
    },

    /**
     * Update active thumbnail visual state
     */
    updateActiveThumbnail(activeIndex) {
      const container = this.$refs.thumbSliderContainer;
      if (!container) return;

      const thumbnails = container.querySelectorAll('.gallery-thumbnail');
      thumbnails.forEach((thumb, index) => {
        if (index === activeIndex) {
          thumb.style.borderColor = 'var(--color-accent)';
        } else {
          thumb.style.borderColor = '';
        }
      });
    },

    /**
     * Calculate thumbnail height from aspect ratio
     */
    calculateThumbHeight() {
      const [width, height] = this.aspectRatio.split('/').map(Number);
      if (width && height) {
        this.thumbHeight = Math.round((this.thumbWidth * height) / width);
      } else {
        this.thumbHeight = 100; // fallback
      }
    },

    /**
     * Sync container height with main product image
     */
    syncWithMainImage() {
      // Find main image container by ID (most reliable)
      const mainImageArea = document.getElementById('main-image-container');

      if (mainImageArea) {
        // Get computed height of main image
        const mainImageHeight = mainImageArea.offsetHeight;
        this.containerHeight = mainImageHeight > 0 ? mainImageHeight : 400;
      } else {
        // Fallback: calculate from aspect ratio and available width
        const container = this.$refs.thumbSliderContainer;
        if (container) {
          const galleryContainer = container.closest('.flex.gap-4');
          if (galleryContainer) {
            const availableWidth = galleryContainer.offsetWidth - this.thumbWidth - 16; // gap
            const [w, h] = this.aspectRatio.split('/').map(Number);
            if (w && h) {
              this.containerHeight = Math.round((availableWidth * h) / w);
            }
          }
        }
      }
    },

    /**
     * Calculate scroll bounds
     */
    calculateScrollBounds() {
      const totalHeight = (this.thumbHeight + this.thumbGap) * this.totalThumbs - this.thumbGap;
      this.maxScroll = Math.max(0, totalHeight - this.containerHeight);

      this.updateScrollButtons();
    },

    /**
     * Update scroll button visibility
     */
    updateScrollButtons() {
      this.canScrollUp = this.scrollOffset > 0;
      this.canScrollDown = this.scrollOffset < this.maxScroll;
    },

    /**
     * Scroll up by one thumbnail
     */
    scrollUp() {
      const scrollAmount = this.thumbHeight + this.thumbGap;
      this.scrollOffset = Math.max(0, this.scrollOffset - scrollAmount);
      this.updateScrollButtons();
    },

    /**
     * Scroll down by one thumbnail
     */
    scrollDown() {
      const scrollAmount = this.thumbHeight + this.thumbGap;
      this.scrollOffset = Math.min(this.maxScroll, this.scrollOffset + scrollAmount);
      this.updateScrollButtons();
    },

    /**
     * Scroll to make specific thumbnail visible
     */
    scrollToThumb(index) {
      const thumbTop = index * (this.thumbHeight + this.thumbGap);
      const thumbBottom = thumbTop + this.thumbHeight;

      // Check if thumb is above visible area
      if (thumbTop < this.scrollOffset) {
        this.scrollOffset = thumbTop;
      }
      // Check if thumb is below visible area
      else if (thumbBottom > this.scrollOffset + this.containerHeight) {
        this.scrollOffset = thumbBottom - this.containerHeight;
      }

      // Ensure bounds
      this.scrollOffset = Math.max(0, Math.min(this.maxScroll, this.scrollOffset));
      this.updateScrollButtons();
    }
  };
}
